import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  LogoutIcon,
  SettingsIcon,
} from '@/components/Icons';
import { LogoutConfirmationModal } from '@/components/LogoutConfirmationModal';
import { PasswordInput } from '@/components/PasswordInput';
import { useSessionState } from '@/context/SessionContext';
import { useThemeState } from '@/context/ThemeContext';
import { changeCurrentPassword } from '@/lib/api';
import { formatInrCurrency } from '@/lib/format';
import { passwordChangeSchema, flattenFieldErrors, getFieldErrorsFromApiError } from '@/lib/validation';
import type { ThemeMode } from '@/lib/types';

const THEME_LABELS: Record<ThemeMode, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

const THEME_HELPERS: Record<ThemeMode, string> = {
  system: 'Follow your device appearance',
  light: 'Always use the bright palette',
  dark: 'Always use the dark palette',
};

function ThemeOption({
  mode,
  selectedMode,
  onPress,
}: {
  mode: ThemeMode;
  selectedMode: ThemeMode;
  onPress: (mode: ThemeMode) => void;
}) {
  const selected = mode === selectedMode;

  return (
    <button
      className={`list-item interactive centered${selected ? ' active' : ''}`}
      type="button"
      onClick={() => onPress(mode)}
    >
      <div>
        <strong>{THEME_LABELS[mode]}</strong>
        <span>{THEME_HELPERS[mode]}</span>
      </div>
      <span className="badge soft">{selected ? 'Selected' : 'Tap to use'}</span>
    </button>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { sessionToken, sessionRole, sessionName, sessionUsername, signOut, isReady: sessionReady, isEnterprise } = useSessionState();
  const { themeMode, setThemeMode, resolvedScheme } = useThemeState();
  const [isSaving, setIsSaving] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordFeedback, setPasswordFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [passwordFieldErrors, setPasswordFieldErrors] = useState<Record<string, string>>({});

  async function handlePasswordChange() {
    if (!sessionToken || isSaving) {
      return;
    }

    const validation = passwordChangeSchema.safeParse(passwordForm);

    if (!validation.success) {
      setPasswordFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
      setPasswordFeedback(null);
      return;
    }

    setIsSaving(true);
    setPasswordFeedback(null);
    setPasswordFieldErrors({});

    try {
      await changeCurrentPassword(sessionToken, {
        currentPassword: validation.data.currentPassword,
        newPassword: validation.data.newPassword,
      });

      setPasswordFeedback({ kind: 'success', message: 'Password updated successfully.' });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      const apiFieldErrors = getFieldErrorsFromApiError(error);

      if (Object.keys(apiFieldErrors).length > 0) {
        setPasswordFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setPasswordFeedback(null);
        return;
      }

      if (error instanceof Error && /current password is incorrect/i.test(error.message)) {
        setPasswordFieldErrors({ currentPassword: error.message });
        setPasswordFeedback(null);
        return;
      }

      setPasswordFeedback({
        kind: 'error',
        message: error instanceof Error && error.message ? error.message : 'Could not update the password.',
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleLogout() {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);

    try {
      await signOut();
      setIsLogoutOpen(false);
      navigate('/login', { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  }

  if (!sessionReady) {
    return <div className="section">Loading settings...</div>;
  }

  if (sessionRole === null) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="section page-grid">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <SettingsIcon size={14} />
            <strong>Settings</strong>
            <span>{resolvedScheme} mode</span>
          </span>

          <h1 className="display-title">
            <span>Account settings</span>
          </h1>

          <p className="copy">Theme preferences, password changes, and sign-out live here.</p>

          <div className="button-row hero-actions">
            <button className="primary-button" type="button" onClick={() => setIsLogoutOpen(true)}>
              <LogoutIcon size={16} />
              Sign out
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Account</div>
            <div className="stat-value">{sessionName ?? 'SlotLooks'}</div>
            <p className="stat-hint">{sessionUsername ?? 'No username saved'}</p>
          </div>
          <div className="stat-card highlight">
            <div className="stat-label">Role</div>
            <div className="stat-value">{sessionRole ?? '--'}</div>
            <p className="stat-hint">Session role stored locally</p>
          </div>
          <div className="stat-card">
            <div className="stat-label">Theme</div>
            <div className="stat-value">{THEME_LABELS[themeMode]}</div>
            <p className="stat-hint">Stored with the browser preferences</p>
          </div>
          <div className="stat-card">
            <div className="stat-label">Access</div>
            <div className="stat-value">{isEnterprise ? 'Admin' : 'Staff'}</div>
            <p className="stat-hint">Controls the visible navigation items</p>
          </div>
        </div>
      </section>

      <section className="content-grid">
        <div className="stack">
          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Password change</h2>
                <p>Uses the same authenticated endpoint as the mobile app.</p>
              </div>
            </div>

            {passwordFeedback ? <div className={`notice ${passwordFeedback.kind}`}>{passwordFeedback.message}</div> : null}

            <div className="field-grid form-stack">
              <label className="field">
                <span className="field-label">Current password</span>
                <PasswordInput
                  className="input"
                  value={passwordForm.currentPassword}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))}
                  autoComplete="current-password"
                />
                {passwordFieldErrors.currentPassword ? <span className="field-error">{passwordFieldErrors.currentPassword}</span> : null}
              </label>

              <div className="split-grid">
                <label className="field">
                  <span className="field-label">New password</span>
                  <PasswordInput
                    className="input"
                    value={passwordForm.newPassword}
                    onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                    autoComplete="new-password"
                  />
                  {passwordFieldErrors.newPassword ? <span className="field-error">{passwordFieldErrors.newPassword}</span> : null}
                </label>

                <label className="field">
                  <span className="field-label">Confirm password</span>
                  <PasswordInput
                    className="input"
                    value={passwordForm.confirmPassword}
                    onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                    autoComplete="new-password"
                  />
                  {passwordFieldErrors.confirmPassword ? <span className="field-error">{passwordFieldErrors.confirmPassword}</span> : null}
                </label>
              </div>

              <div className="button-row">
                <button className="primary-button" type="button" onClick={handlePasswordChange} disabled={isSaving || !sessionToken}>
                  {isSaving ? 'Saving...' : 'Update password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LogoutConfirmationModal
        open={isLogoutOpen}
        isSigningOut={isSigningOut}
        accountName={sessionName ?? sessionUsername}
        onCancel={() => setIsLogoutOpen(false)}
        onConfirm={() => {
          void handleLogout();
        }}
      />
    </div>
  );
}
